import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { addressService } from '../services/addressService';
import { checkoutService } from '../services/checkoutService';
import { supportService, TICKET_CATEGORIES, TICKET_STATUS } from '../services/supportService';
import AddressForm from '../components/checkout/AddressForm';
import ConfirmModal from '../components/common/ConfirmModal';
import './Profile.css';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'orders';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  // Support state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [rateLimits, setRateLimits] = useState({ remainingTickets: 5, remainingMessages: 20 });
  const [ticketForm, setTicketForm] = useState({
    category: '',
    subject: '',
    description: '',
    orderId: '',
  });
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [ticketError, setTicketError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, addressId: null });

  // Sync activeTab with URL query param
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'addresses') {
      fetchAddresses();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'support') {
      fetchTickets();
      fetchRateLimits();
    }
  }, [activeTab]);

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await checkoutService.getMyOrders();
      if (response.success) {
        setOrders(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await supportService.getMyTickets();
      if (response.success) {
        setTickets(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchRateLimits = async () => {
    try {
      const response = await supportService.getRateLimits();
      if (response.success) {
        setRateLimits(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch rate limits:', err);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await supportService.getTicket(ticketId);
      if (response.success) {
        setSelectedTicket(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch ticket:', err);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.category || !ticketForm.subject || !ticketForm.description) {
      setTicketError('Please fill all required fields');
      return;
    }
    setCreatingTicket(true);
    setTicketError(null);
    try {
      const response = await supportService.createTicket({
        category: ticketForm.category,
        subject: ticketForm.subject,
        description: ticketForm.description,
        orderId: ticketForm.orderId || null,
      });
      if (response.success) {
        setTickets(prev => [response.data, ...prev]);
        setShowCreateTicket(false);
        setTicketForm({ category: '', subject: '', description: '', orderId: '' });
        fetchRateLimits();
      } else {
        setTicketError(response.message || 'Failed to create ticket');
      }
    } catch (err) {
      setTicketError(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSendingMessage(true);
    try {
      const response = await supportService.addMessage(selectedTicket.id, newMessage);
      if (response.success) {
        setSelectedTicket(prev => ({
          ...prev,
          messages: [...(prev.messages || []), response.data],
        }));
        setNewMessage('');
        fetchRateLimits();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      alert(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchAddresses = async () => {
    setAddressesLoading(true);
    setAddressError(null);
    try {
      const response = await addressService.getAddresses();
      if (response.success) {
        setAddresses(response.data || []);
      } else {
        setAddressError(response.message || 'Failed to load addresses');
      }
    } catch (err) {
      setAddressError(err.response?.data?.message || 'Failed to load addresses');
    } finally {
      setAddressesLoading(false);
    }
  };

  const handleAddAddress = async (formData) => {
    setFormLoading(true);
    try {
      const response = await addressService.createAddress(formData);
      if (response.success) {
        setAddresses(prev => [...prev, response.data]);
        setShowAddressForm(false);
        if (response.data.isDefault) {
          setAddresses(prev => prev.map(a => ({
            ...a,
            isDefault: a.id === response.data.id
          })));
        }
      }
    } catch (err) {
      setAddressError(err.response?.data?.message || 'Failed to add address');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateAddress = async (formData) => {
    if (!editingAddress) return;
    setFormLoading(true);
    try {
      const response = await addressService.updateAddress(editingAddress.id, formData);
      if (response.success) {
        setAddresses(prev => prev.map(a =>
          a.id === editingAddress.id ? response.data : a
        ));
        setEditingAddress(null);
        setShowAddressForm(false);
      }
    } catch (err) {
      setAddressError(err.response?.data?.message || 'Failed to update address');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteAddress = (addressId) => {
    setDeleteModal({ isOpen: true, addressId });
  };

  const confirmDeleteAddress = async () => {
    const addressId = deleteModal.addressId;
    try {
      const response = await addressService.deleteAddress(addressId);
      if (response.success) {
        setAddresses(prev => prev.filter(a => a.id !== addressId));
      }
    } catch (err) {
      setAddressError(err.response?.data?.message || 'Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await addressService.setDefaultAddress(addressId);
      if (response.success) {
        setAddresses(prev => prev.map(a => ({
          ...a,
          isDefault: a.id === addressId
        })));
      }
    } catch (err) {
      setAddressError(err.response?.data?.message || 'Failed to set default address');
    }
  };

  const handleEditClick = (address) => {
    setEditingAddress(address);
    setShowAddressForm(true);
  };

  const handleFormCancel = () => {
    setShowAddressForm(false);
    setEditingAddress(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="profile-page">
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, addressId: null })}
        onConfirm={confirmDeleteAddress}
        title="Delete Address"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <div className="profile-container">
        {/* Profile Hero - Always visible */}
        <motion.div
          className="profile-hero"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              {user?.name ? user.name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="profile-hero-info">
              <h2 className="profile-hero-name">{user?.name || 'N/A'}</h2>
              <p className="profile-hero-email">{user?.email || 'N/A'}</p>
              <span className="profile-status-badge">
                <span className="status-dot" />
                Verified
              </span>
            </div>
          </div>
          <button className="logout-button-compact" onClick={handleLogout}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </motion.div>

        <div className="profile-content">
          <div className="profile-tabs">
            <button
              className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              Orders
            </button>
            <button
              className={`tab ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              Addresses
            </button>
            <button
              className={`tab ${activeTab === 'support' ? 'active' : ''}`}
              onClick={() => setActiveTab('support')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Support
            </button>
          </div>

          <div className="profile-tab-content">
            {activeTab === 'orders' && (
              <motion.div
                className="tab-panel"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="orders-list">
                  {ordersLoading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <span>Loading orders...</span>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">📦</div>
                      <h3>No orders yet</h3>
                      <p>Start designing to place your first order</p>
                      <button
                        className="cta-button primary"
                        onClick={() => navigate('/design')}
                      >
                        Start Designing
                      </button>
                    </div>
                  ) : (
                    orders.map((order, index) => (
                      <motion.div
                        key={order.id}
                        className={`order-card ${order.status === 'CANCELLED' || order.status === 'REFUNDED' ? 'order-card-refund' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="order-card-top">
                          <div className="order-header">
                            <div className="order-info">
                              <div className="order-id">{order.orderNumber}</div>
                              <div className="order-date">
                                Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                            <div className={`order-status ${order.status.toLowerCase().replace(/_/g, '-')}`}>
                              {order.status === 'CANCELLED' && <span className="status-icon">✕</span>}
                              {order.status === 'REFUNDED' && <span className="status-icon">↩</span>}
                              {order.status === 'RETURNED' && <span className="status-icon">↩</span>}
                              {order.status === 'DELIVERED' && <span className="status-icon">✓</span>}
                              {order.status === 'OUT_FOR_DELIVERY' && <span className="status-icon">🚚</span>}
                              {order.status === 'SHIPPED' && <span className="status-icon">📦</span>}
                              {order.status === 'PROCESSING' && <span className="status-icon">⚙</span>}
                              {order.status === 'PENDING' && <span className="status-icon">⏳</span>}
                              {order.status === 'CONFIRMED' && <span className="status-icon">✓</span>}
                              {order.status === 'DRAFT' && <span className="status-icon">📝</span>}
                              <span className="status-text">{order.status.replace(/_/g, ' ')}</span>
                            </div>
                          </div>

                          {/* Refund Card for cancelled/refunded orders */}
                          {order.status === 'CANCELLED' && (
                            <div className="refund-card refund-pending">
                              <div className="refund-card-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"/>
                                  <polyline points="12 6 12 12 16 14"/>
                                </svg>
                              </div>
                              <div className="refund-card-content">
                                <div className="refund-card-title">Refund Processing</div>
                                <div className="refund-card-amount">₹{parseFloat(order.total).toLocaleString()}</div>
                                <div className="refund-card-desc">
                                  Your refund is being processed and will be credited to your original payment method within 5-7 business days.
                                </div>
                              </div>
                            </div>
                          )}
                          {order.status === 'REFUNDED' && (
                            <div className="refund-card refund-completed">
                              <div className="refund-card-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                  <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                              </div>
                              <div className="refund-card-content">
                                <div className="refund-card-title">Refund Completed</div>
                                <div className="refund-card-amount">₹{parseFloat(order.total).toLocaleString()}</div>
                                <div className="refund-card-desc">
                                  The amount has been successfully refunded to your original payment method.
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="order-summary-row">
                            <div className="order-items-count">
                              <span className="items-icon">🛒</span>
                              {order.items?.length || 0} item{(order.items?.length || 0) > 1 ? 's' : ''}
                            </div>
                            <div className="order-total-amount">
                              <span className="total-label">Total Paid</span>
                              <span className="total-value">₹{parseFloat(order.total).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        <button
                          className={`view-order-button ${expandedOrderId === order.id ? 'expanded' : ''}`}
                          onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                        >
                          <span>{expandedOrderId === order.id ? 'Hide Details' : 'View Details'}</span>
                          <svg
                            className="chevron-icon"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        </button>

                        {/* Expanded Order Details */}
                        <AnimatePresence>
                          {expandedOrderId === order.id && (
                            <motion.div
                              className="order-expanded-details"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="order-items-section">
                                <h4>Order Items</h4>
                                {order.items?.map((item, idx) => (
                                  <div key={idx} className="order-item-row">
                                    <div className="item-details">
                                      <span className="item-name">{item.productVariantName || 'Custom Lamp'}</span>
                                      <span className="item-qty">Qty: {item.quantity}</span>
                                    </div>
                                    <span className="item-price">₹{parseFloat(item.subtotal || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>

                              <div className="order-summary-section">
                                <div className="summary-row">
                                  <span>Subtotal</span>
                                  <span>₹{parseFloat(order.subtotal || 0).toLocaleString()}</span>
                                </div>
                                <div className="summary-row">
                                  <span>Shipping</span>
                                  <span>₹{parseFloat(order.shippingCost || 0).toLocaleString()}</span>
                                </div>
                                <div className="summary-row total">
                                  <span>Total</span>
                                  <span>₹{parseFloat(order.total || 0).toLocaleString()}</span>
                                </div>
                              </div>

                              {order.shippingAddress && (
                                <div className="order-address-section">
                                  <h4>Shipping Address</h4>
                                  <div className="address-block">
                                    <p className="address-name">{order.shippingAddress.fullName}</p>
                                    <p>{order.shippingAddress.addressLine1}</p>
                                    {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                                    <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</p>
                                  </div>
                                </div>
                              )}

                              {order.trackingNumber && (
                                <div className="order-tracking-section">
                                  <h4>Tracking Information</h4>
                                  <div className="tracking-block">
                                    <span className="tracking-label">Tracking Number:</span>
                                    <span className="tracking-number">{order.trackingNumber}</span>
                                  </div>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'addresses' && (
              <motion.div
                className="tab-panel"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {addressError && (
                  <div className="error-message">
                    {addressError}
                    <button onClick={() => setAddressError(null)}>×</button>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {showAddressForm ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <AddressForm
                        initialData={editingAddress}
                        onSubmit={editingAddress ? handleUpdateAddress : handleAddAddress}
                        onCancel={handleFormCancel}
                        isLoading={formLoading}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="list"
                      className="addresses-list"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {addressesLoading ? (
                        <div className="loading-state">
                          <div className="loading-spinner"></div>
                          <span>Loading addresses...</span>
                        </div>
                      ) : addresses.length === 0 ? (
                        <div className="empty-state">
                          <div className="empty-icon">📍</div>
                          <h3>No addresses saved</h3>
                          <p>Add an address for faster checkout</p>
                        </div>
                      ) : (
                        addresses.map((address, index) => (
                          <motion.div
                            key={address.id}
                            className="address-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            {address.isDefault && (
                              <div className="default-badge">Default</div>
                            )}
                            <div className="address-type-badge">{address.addressType}</div>
                            <div className="address-content">
                              <div className="address-name">{address.fullName}</div>
                              <div className="address-text">
                                {address.addressLine1}
                                {address.addressLine2 && `, ${address.addressLine2}`}
                              </div>
                              {address.landmark && (
                                <div className="address-text landmark">
                                  Landmark: {address.landmark}
                                </div>
                              )}
                              <div className="address-text">
                                {address.city}, {address.state} - {address.pincode}
                              </div>
                              <div className="address-mobile">{address.mobile}</div>
                            </div>
                            <div className="address-actions">
                              <button
                                className="edit-button"
                                onClick={() => handleEditClick(address)}
                              >
                                Edit
                              </button>
                              {!address.isDefault && (
                                <>
                                  <button
                                    className="default-button"
                                    onClick={() => handleSetDefault(address.id)}
                                  >
                                    Set Default
                                  </button>
                                  <button
                                    className="delete-button"
                                    onClick={() => handleDeleteAddress(address.id)}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </motion.div>
                        ))
                      )}
                      <motion.button
                        className="add-address-button"
                        onClick={() => setShowAddressForm(true)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        + Add New Address
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {activeTab === 'support' && (
              <motion.div
                className="tab-panel"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <AnimatePresence mode="wait">
                  {/* Create Ticket Form */}
                  {showCreateTicket ? (
                    <motion.div
                      key="create-form"
                      className="create-ticket-form"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <h3>Create Support Ticket</h3>
                      {ticketError && (
                        <div className="error-message">
                          {ticketError}
                          <button onClick={() => setTicketError(null)}>×</button>
                        </div>
                      )}
                      <form onSubmit={handleCreateTicket}>
                        <div className="form-group">
                          <label>Category *</label>
                          <select
                            value={ticketForm.category}
                            onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                            required
                          >
                            <option value="">Select a category</option>
                            {TICKET_CATEGORIES.map((cat) => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {(ticketForm.category === 'ORDER' || ticketForm.category === 'SHIPPING' || ticketForm.category === 'REFUND') && orders.length > 0 && (
                          <div className="form-group">
                            <label>Related Order (Optional)</label>
                            <select
                              value={ticketForm.orderId}
                              onChange={(e) => setTicketForm({ ...ticketForm, orderId: e.target.value })}
                            >
                              <option value="">Select an order</option>
                              {orders.map((order) => (
                                <option key={order.id} value={order.id}>
                                  {order.orderNumber} - {order.status}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="form-group">
                          <label>Subject *</label>
                          <input
                            type="text"
                            value={ticketForm.subject}
                            onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                            placeholder="Brief description of your issue"
                            maxLength={255}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Description *</label>
                          <textarea
                            value={ticketForm.description}
                            onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                            placeholder="Please describe your issue in detail..."
                            rows={5}
                            maxLength={5000}
                            required
                          />
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="cancel-btn"
                            onClick={() => {
                              setShowCreateTicket(false);
                              setTicketForm({ category: '', subject: '', description: '', orderId: '' });
                              setTicketError(null);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="submit-btn"
                            disabled={creatingTicket || rateLimits.remainingTickets <= 0}
                          >
                            {creatingTicket ? 'Creating...' : 'Create Ticket'}
                          </button>
                        </div>
                        <div className="rate-limit-info">
                          Remaining tickets today: {rateLimits.remainingTickets}
                        </div>
                      </form>
                    </motion.div>
                  ) : selectedTicket ? (
                    /* Ticket Detail View */
                    <motion.div
                      key="ticket-detail"
                      className="ticket-detail"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <button className="back-btn" onClick={() => setSelectedTicket(null)}>
                        ← Back to Tickets
                      </button>
                      <div className="ticket-detail-header">
                        <div className="ticket-info">
                          <span className="ticket-number">{selectedTicket.ticketNumber}</span>
                          <span
                            className="ticket-status-badge"
                            style={{ backgroundColor: TICKET_STATUS[selectedTicket.status]?.color || '#6b7280' }}
                          >
                            {TICKET_STATUS[selectedTicket.status]?.label || selectedTicket.status}
                          </span>
                        </div>
                        <h3>{selectedTicket.subject}</h3>
                        <div className="ticket-meta">
                          <span>{TICKET_CATEGORIES.find(c => c.value === selectedTicket.category)?.label || selectedTicket.category}</span>
                          <span>Created: {new Date(selectedTicket.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="messages-container">
                        <div className="message initial-message">
                          <div className="message-header">
                            <span className="sender-name">You</span>
                            <span className="message-time">
                              {new Date(selectedTicket.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="message-content">{selectedTicket.description}</div>
                        </div>

                        {selectedTicket.messages?.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message ${msg.senderType === 'SUPPORT' ? 'support-message' : 'user-message'}`}
                          >
                            <div className="message-header">
                              <span className="sender-name">
                                {msg.senderType === 'SUPPORT' ? 'Support Team' : 'You'}
                              </span>
                              <span className="message-time">
                                {new Date(msg.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <div className="message-content">{msg.message}</div>
                          </div>
                        ))}
                      </div>

                      {selectedTicket.status !== 'CLOSED' && (
                        <div className="reply-box">
                          <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            rows={3}
                            maxLength={5000}
                          />
                          <div className="reply-actions">
                            <span className="char-count">{newMessage.length}/5000</span>
                            <button
                              className="send-btn"
                              onClick={handleSendMessage}
                              disabled={sendingMessage || !newMessage.trim() || rateLimits.remainingMessages <= 0}
                            >
                              {sendingMessage ? 'Sending...' : 'Send'}
                            </button>
                          </div>
                          <div className="rate-limit-info">
                            Remaining messages today: {rateLimits.remainingMessages}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    /* Tickets List */
                    <motion.div
                      key="tickets-list"
                      className="tickets-list"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {ticketsLoading ? (
                        <div className="loading-state">
                          <div className="loading-spinner"></div>
                          <span>Loading tickets...</span>
                        </div>
                      ) : tickets.length === 0 ? (
                        <div className="empty-state">
                          <div className="empty-icon">💬</div>
                          <h3>No support tickets</h3>
                          <p>Need help? Create a support ticket</p>
                        </div>
                      ) : (
                        tickets.map((ticket, index) => (
                          <motion.div
                            key={ticket.id}
                            className="ticket-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => fetchTicketDetails(ticket.id)}
                          >
                            <div className="ticket-card-header">
                              <span className="ticket-number">{ticket.ticketNumber}</span>
                              <span
                                className="ticket-status-badge"
                                style={{ backgroundColor: TICKET_STATUS[ticket.status]?.color || '#6b7280' }}
                              >
                                {TICKET_STATUS[ticket.status]?.label || ticket.status}
                              </span>
                            </div>
                            <h4 className="ticket-subject">{ticket.subject}</h4>
                            <div className="ticket-card-footer">
                              <span className="ticket-category">
                                {TICKET_CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}
                              </span>
                              <span className="ticket-date">
                                {new Date(ticket.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </motion.div>
                        ))
                      )}
                      <motion.button
                        className="create-ticket-button"
                        onClick={() => {
                          if (orders.length === 0) fetchOrders();
                          setShowCreateTicket(true);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={rateLimits.remainingTickets <= 0}
                      >
                        + Create Support Ticket
                      </motion.button>
                      {rateLimits.remainingTickets <= 0 && (
                        <div className="rate-limit-warning">
                          You have reached the daily ticket limit. Please try again tomorrow.
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
